import { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Container } from '@/components/layout/Container';
import { Section } from '@/components/layout/Section';
import { Heading } from '@/components/layout/Heading';
import { RouteHead } from '@/components/seo/RouteHead';
import { ROUTES } from '@/routes';
import { fadeIn, fadeUp, staggerContainer, STAGGER, VIEWPORT_REVEAL } from '@/lib/motion';
import { useReleases } from '@/lib/releases';
import { ReleaseEntry } from './ReleaseEntry';

const PAGE_SIZE = 10;

export default function ChangelogPage() {
  const { t } = useTranslation('changelog');
  const { data: releases, isPending, isError } = useReleases();
  const [shown, setShown] = useState(PAGE_SIZE);

  const total = releases?.length ?? 0;
  const visible = releases?.slice(0, shown) ?? [];
  const hasMore = shown < total;

  return (
    <>
      <RouteHead
        path={ROUTES.changelog}
        titleKey="seo:changelog.title"
        descriptionKey="seo:changelog.description"
      />
      <Section padding="lg">
        <Container size="md" className="text-center">
          <motion.div
            variants={staggerContainer(STAGGER.base)}
            initial="hidden"
            animate="visible"
          >
            <motion.p
              variants={fadeIn}
              className="mb-4 text-xs font-semibold uppercase tracking-widest text-brand"
            >
              {t('page.eyebrow')}
            </motion.p>
            <motion.div variants={fadeUp}>
              <Heading level={1}>{t('page.headline')}</Heading>
            </motion.div>
            <motion.p
              variants={fadeUp}
              className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground"
            >
              {t('page.subheadline')}
            </motion.p>
          </motion.div>
        </Container>
      </Section>

      <Section padding="lg" bordered>
        <Container size="md">
          {isPending ? (
            <p
              role="status"
              aria-live="polite"
              className="text-center text-sm text-muted-foreground"
            >
              {t('page.loading')}
            </p>
          ) : isError ? (
            <div role="alert" className="mx-auto max-w-md text-center">
              <p className="font-semibold">{t('page.error_headline')}</p>
              <p className="mt-1 text-sm text-muted-foreground">{t('page.error_body')}</p>
            </div>
          ) : total === 0 ? (
            <div className="mx-auto max-w-md text-center">
              <p className="font-semibold">{t('page.empty_headline')}</p>
              <p className="mt-1 text-sm text-muted-foreground">{t('page.empty_body')}</p>
            </div>
          ) : (
            <motion.div
              variants={staggerContainer(STAGGER.tight)}
              initial="hidden"
              whileInView="visible"
              viewport={VIEWPORT_REVEAL}
              className="flex flex-col gap-6"
            >
              {visible.map((r) => (
                <motion.div key={r.id ?? r.version} variants={fadeUp}>
                  <ReleaseEntry release={r} />
                </motion.div>
              ))}

              <div className="mt-2 flex flex-col items-center gap-3">
                <p className="text-xs text-muted-foreground">
                  {t('pagination.showing', { shown: visible.length, total })}
                </p>
                {hasMore ? (
                  <button
                    type="button"
                    onClick={() => setShown((n) => n + PAGE_SIZE)}
                    className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-2.5 text-sm font-semibold transition-colors duration-fast ease-out-quart hover:border-brand/50 hover:text-brand focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
                  >
                    {t('pagination.load_more')}
                  </button>
                ) : null}
              </div>
            </motion.div>
          )}
        </Container>
      </Section>
    </>
  );
}
